import type { FC } from 'react';
import React from 'react';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import DottedTitle from '@pages/Settings/components/GeneralSettings/components/DottedTitle';
import { InfoIcon } from '@setup/svg-icons/svg-icons';
import { MuiTooltip } from '@shared/ui-kit';

import type { TypographyOwnProps } from '@mui/material';

type LargeContainerProps = {
  children?: React.ReactNode;
  title: string;
  alignItems?: 'center' | 'flexStart' | 'flexEnd';
  titlePadding?: number | string;
  isFlexWidth?: boolean;
  isRequired?: boolean;
  titleInsideContainer?: boolean;
  descriptionLargeContainer?: string;
  descriptionTypographyVariant?: TypographyOwnProps['variant'];
  customWidthLeftTitle?: string;
  titleVariant?: TypographyOwnProps['variant'];
  infoIconTooltipText?: string;
  titleWithDotted?: boolean;
  customTitleMarginBottom?: string;
  descriptionLargeContainerWidth?: string;
};

const LargeContainer: FC<LargeContainerProps> = ({
  children,
  title,
  alignItems = 'center',
  titlePadding = 0,
  isFlexWidth,
  isRequired,
  titleInsideContainer,
  descriptionLargeContainer,
  descriptionTypographyVariant,
  customWidthLeftTitle,
  titleVariant,
  infoIconTooltipText,
  titleWithDotted = false,
  customTitleMarginBottom,
  descriptionLargeContainerWidth,
}) => {
  const theme = useTheme();
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('md'));
  const isShowLeftTitle = !titleInsideContainer && !isSmallDisplay;
  const isShowTitleInsideContainer = titleInsideContainer || isSmallDisplay;

  const descriptionElement = (
    <Typography
      sx={{
        width: isSmallDisplay ? 'initial' : descriptionLargeContainerWidth || '160px',
        flexShrink: '0',
        paddingTop: '4px',
      }}
      color={theme.palette.case.neutral.n500}
      variant={descriptionTypographyVariant ? descriptionTypographyVariant : 't16r'}
    >
      {descriptionLargeContainer}
    </Typography>
  );

  return (
    <Box display="flex" alignItems={alignItems} width="100%">
      {isShowLeftTitle && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              width: customWidthLeftTitle || '160px',
              flexShrink: '0',
              paddingTop: `${titlePadding}px`,
              mr: customWidthLeftTitle ? '0' : '24px',
              display: 'flex',
              svg: {
                height: '16px',
                width: '16px',
                path: {
                  fill: theme.palette.case.neutral.n500,
                },
              },
            }}
            color={theme.palette.case.neutral.n900}
            variant="s2"
          >
            {title}
            {isRequired && (
              <Box component="span" sx={{ color: theme.palette.case.red.r600 }}>
                {' *'}
              </Box>
            )}

            {infoIconTooltipText && (
              <MuiTooltip color="dark" title={infoIconTooltipText}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: { xs: '4px', sm: '6px' },
                  }}
                >
                  <InfoIcon />
                </Box>
              </MuiTooltip>
            )}
          </Typography>
          {descriptionLargeContainer && descriptionElement}
        </Box>
      )}

      <Box sx={{ width: isFlexWidth ? { xs: '100%', sm: 'calc(100% - 160px)' } : '100%' }}>
        {isShowTitleInsideContainer && (
          <Box
            mb={!title ? '0px' : customTitleMarginBottom || '16px'}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            {titleWithDotted && <DottedTitle title={title} titleVariant={titleVariant} />}
            {!titleWithDotted && <Typography variant={titleVariant || 's2'}>{title}</Typography>}
            {descriptionLargeContainer && descriptionElement}
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default LargeContainer;
